import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Outlet, useLocation } from 'react-router-dom';
import { Box, Divider, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { useGetAgencyById } from '@frontend/features/agencies/queries';

import { routes } from '../routes';
import { EditAgencyModal } from '@frontend/features/agencies/components/EditAgency';
import { useState } from 'react';
import { BackButton } from '@frontend/features/common/components/BackButton';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import ShutterSpeedIcon from '@mui/icons-material/ShutterSpeed';
import {
  Architecture,
  Assignment,
  Business,
  Chat,
  Flight,
  Groups,
  Hotel,
  ListAlt,
  Loop,
  PermContactCalendar,
  Phone,
  Storefront,
  WhatsApp,
  Upload,
  Psychology,
  FlightTakeoff,
  Hub,
  Campaign,
  AutoAwesome,
  MenuBook,
  TravelExplore,
  ConfirmationNumber,
} from '@mui/icons-material';
import { DataBox } from '@frontend/features/common/components/DataBox';
import { SideList, SideListItemButton } from '@frontend/features/common/components/SideList';
import { Can } from '@casl/react';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { UserRoles } from '@bandido/shared/types';

export const AgencyView = () => {
  const theme = useTheme();
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { ability, myProfile } = useAuth();
  const isAdmin = Boolean(myProfile?.roles?.split(',').includes(UserRoles.ADMINS));

  const workflowViewRoute = new RegExp('^/dashboard/agencies/[^/]+/workflows/[^/]+$');
  const callsMenuRoute = new RegExp('^/dashboard/agencies/[^/]+/calls-menu$');

  const isWorkflowViewPage = workflowViewRoute.test(location.pathname);
  const isCallsMenuPage = callsMenuRoute.test(location.pathname);
  const isViewPage = isWorkflowViewPage || isCallsMenuPage;

  const toggleIsOpenEdit = () => setIsOpenEdit((prevState) => !prevState);

  const { data } = useGetAgencyById({ id });

  return (
    <Box
      display="flex"
      gap={isViewPage ? 0 : 8}
      sx={{
        height: 'calc(100vh - 64px)',
        backgroundColor: theme.palette.paper.dark,
      }}
      overflow="hidden"
    >
      <OverlayScrollbarsComponent
        style={{
          minWidth: 300,
          backgroundColor: theme.palette.paper.dark,
          boxSizing: 'border-box',
          height: '100%',
          boxShadow: theme.shadows[1],
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'scroll', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start" alignSelf="stretch" p={2} gap={3}>
          <Box display="flex" gap={1} alignItems="center" mt={1}>
            <IconAvatar size="medium" backgroundColor={theme.palette.divider}>
              <Storefront htmlColor={theme.palette.common.white} />
            </IconAvatar>
            <DataBox
              sx={{
                gap: 0,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="subtitle2" color="text.primary">
                  {data?.name}
                </Typography>

                {/* 
                TODO: borrar cuando se implmente los settings estos en el form de settings
                <IconButton onClick={toggleIsOpenEdit}>
                <EditIcon htmlColor={theme.palette.action.active} />
              </IconButton> */}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {data?.country}, {data?.currency}
              </Typography>
            </DataBox>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Divider aria-hidden="true" textAlign="left" sx={{ mb: 3 }} />
            <SideList aria-label={`agency menu ${t('agencies.view.general')}`} dense disablePadding>
              <Can I="create" a="CasePublicFilters" ability={ability}>
                <SideListItemButton
                  selected={Boolean(id && location.pathname === routes.dashboard.agencyView.root.replace(':id', id))}
                  onClick={() => id && navigate(routes.dashboard.agencyView.root.replace(':id', id))}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <PermContactCalendar />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.generalSettings')} />
                </SideListItemButton>
                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.communications
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.communications
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Chat />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.communications')} />
                </SideListItemButton>
                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.callsMenu
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.callsMenu}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.callsMenu')} />
                </SideListItemButton>
                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.iaConfig
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.iaConfig}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Psychology />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.iaConfig')} />
                </SideListItemButton>
                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.whatsappSettings
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.whatsappSettings
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <WhatsApp />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.whatsappSettings')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.copilot
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.copilot
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <AutoAwesome />
                  </ListItemIcon>
                  <ListItemText primary={t('copilot.tabLabel')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.rutero
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.rutero
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <TravelExplore />
                  </ListItemIcon>
                  <ListItemText primary={t('rutero.tabLabel')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.hotels
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.hotels
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Hotel />
                  </ListItemIcon>
                  <ListItemText primary={t('hotelConnections.title')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.vouchers
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.vouchers
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <ConfirmationNumber />
                  </ListItemIcon>
                  <ListItemText primary={t('vouchers.config.tabLabel')} />
                </SideListItemButton>

                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.agencyKnowledge
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.agencyKnowledge
                      }`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <MenuBook />
                  </ListItemIcon>
                  <ListItemText primary="Conocimiento IA" />
                </SideListItemButton>

              <SideListItemButton
                selected={Boolean(
                  id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.caseTriggers
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.caseTriggers
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                <ShutterSpeedIcon />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.caseTriggers')} />
              </SideListItemButton>


                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.uploads}`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.uploads}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Upload />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.uploads')} />
                </SideListItemButton>
                <Divider />
              </Can>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.teams}`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.teams}`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Groups />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.teams')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.iaCaseEventHandlers
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.iaCaseEventHandlers
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Psychology />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.caseHandlers')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.workflows}`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.workflows}`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Loop />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.workflows')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.organizations
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.organizations
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Business />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.organizations')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.supportForm
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.supportForm}`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Assignment />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.supportForms')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.customFields
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.customFields
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Architecture />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.customFields')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.markupRulesList
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.markupRulesList
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Flight />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.markupRules')} />
              </SideListItemButton>

              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.airRoutingRules
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.airRoutingRules
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Hotel />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.routingRules')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.invalidationRules
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.invalidationRules
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <ListAlt />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.invalidationRules')} />
              </SideListItemButton>
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.agencyFlightIntegrations
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                      routes.dashboard.agencyView.agencyFlightIntegrations
                    }`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <FlightTakeoff />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.flightIntegrations')} />
              </SideListItemButton>
              {isAdmin && (
                <SideListItemButton
                  selected={Boolean(
                    id &&
                      location.pathname.includes(
                        `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                          routes.dashboard.agencyView.webhooks
                        }`,
                      ),
                  )}
                  onClick={() =>
                    id &&
                    navigate(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.webhooks}`,
                    )
                  }
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                    <Hub />
                  </ListItemIcon>
                  <ListItemText primary={t('agencies.view.webhooks')} />
                </SideListItemButton>
              )}
              <SideListItemButton
                selected={Boolean(
                  id &&
                    location.pathname.includes(
                      `${routes.dashboard.agencyView.root.replace(':id', id)}/${
                        routes.dashboard.agencyView.metaCapi
                      }`,
                    ),
                )}
                onClick={() =>
                  id &&
                  navigate(
                    `${routes.dashboard.agencyView.root.replace(':id', id)}/${routes.dashboard.agencyView.metaCapi}`,
                  )
                }
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '35px' }}>
                  <Campaign />
                </ListItemIcon>
                <ListItemText primary={t('agencies.view.metaAds')} />
              </SideListItemButton>
            </SideList>
          </Box>
        </Box>
      </OverlayScrollbarsComponent>

      {!isViewPage ? (
        <Box
          sx={{
            overflowX: 'auto',
            width: '100%',
            minWidth: 0,
            pr: 2,
            // pt: 3,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              mt: 2,
              mb: 2,
            }}
          >
            <BackButton backRoute={routes.dashboard.agencies} />
          </Box>

          <Outlet />
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      )}
      {id && <EditAgencyModal agencyId={Number(id)} open={isOpenEdit} onClose={toggleIsOpenEdit} />}
    </Box>
  );
};
